import { useCallback, useEffect, useState } from "react";
import AfiliadoEstadoBadge from "@/pages/admin/components/AfiliadoEstadoBadge";
import { getAfiliadoEstadoMeta, tipoAfiliadoLabel } from "@/lib/affiliateEstados";
import { formatFechaHora } from "@/lib/format";
import { supabase } from "@/lib/supabase";
import AfiliadoResumen from "./AfiliadoResumen";
import AfiliadoVehiculos from "./AfiliadoVehiculos";
import AfiliadoDocumentos from "./AfiliadoDocumentos";
import AfiliadoHistorial from "./AfiliadoHistorial";
import type {
  Afiliado,
  AfiliadoDisponibilidad,
  AfiliadoDocumento,
  AfiliadoHistorial as HistorialRow,
  AfiliadoNota,
  AfiliadoServicio,
  AfiliadoVehiculo,
} from "@/types";

interface AfiliadoDetalleProps {
  afiliado: Afiliado;
  onClose: () => void;
  onEstadoChange: (id: string, estado: string) => void;
}

type TabId = "resumen" | "vehiculos" | "documentos" | "historial";

const tabs: { id: TabId; label: string; icon: string }[] = [
  { id: "resumen", label: "Resumen", icon: "ri-profile-line" },
  { id: "vehiculos", label: "Vehículos", icon: "ri-truck-line" },
  { id: "documentos", label: "Documentos", icon: "ri-folder-shield-line" },
  { id: "historial", label: "Historial y notas", icon: "ri-history-line" },
];

const acciones: { label: string; estado: string; icon: string; tone: "primary" | "outline" }[] = [
  { label: "En revisión", estado: "en_revision", icon: "ri-search-eye-line", tone: "outline" },
  { label: "Solicitar información", estado: "info_adicional", icon: "ri-question-line", tone: "outline" },
  { label: "Aprobar afiliación", estado: "aprobado", icon: "ri-checkbox-circle-line", tone: "primary" },
  { label: "Activar", estado: "activo", icon: "ri-rocket-line", tone: "primary" },
  { label: "Suspender", estado: "suspendido", icon: "ri-pause-circle-line", tone: "outline" },
  { label: "Rechazar", estado: "rechazado", icon: "ri-close-circle-line", tone: "outline" },
];

export default function AfiliadoDetalle({ afiliado, onClose, onEstadoChange }: AfiliadoDetalleProps) {
  const [tab, setTab] = useState<TabId>("resumen");
  const [loading, setLoading] = useState(true);
  const [dataError, setDataError] = useState("");
  const [actionError, setActionError] = useState("");
  const [updating, setUpdating] = useState(false);
  const [addingNote, setAddingNote] = useState(false);
  const [current, setCurrent] = useState<Afiliado>(afiliado);

  const [vehiculos, setVehiculos] = useState<AfiliadoVehiculo[]>([]);
  const [documentos, setDocumentos] = useState<AfiliadoDocumento[]>([]);
  const [disponibilidad, setDisponibilidad] = useState<AfiliadoDisponibilidad[]>([]);
  const [historial, setHistorial] = useState<HistorialRow[]>([]);
  const [notas, setNotas] = useState<AfiliadoNota[]>([]);
  const [, setServicios] = useState<AfiliadoServicio[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setDataError("");
    try {
      const [veh, docs, disp, hist, notes, servs] = await Promise.all([
        supabase.from("affiliate_vehicles").select("*").eq("affiliate_id", afiliado.id).order("orden"),
        supabase.from("affiliate_documents").select("*").eq("affiliate_id", afiliado.id).order("creado_en"),
        supabase.from("affiliate_availability").select("*").eq("affiliate_id", afiliado.id),
        supabase
          .from("affiliate_status_history")
          .select("*")
          .eq("affiliate_id", afiliado.id)
          .order("creado_en", { ascending: false }),
        supabase
          .from("affiliate_notes")
          .select("*")
          .eq("affiliate_id", afiliado.id)
          .order("creado_en", { ascending: false }),
        supabase.from("affiliate_services").select("*").eq("affiliate_id", afiliado.id),
      ]);
      if (veh.error || docs.error || disp.error || hist.error || notes.error || servs.error) {
        throw new Error("load_failed");
      }
      setVehiculos((veh.data as AfiliadoVehiculo[]) ?? []);
      setDocumentos((docs.data as AfiliadoDocumento[]) ?? []);
      setDisponibilidad((disp.data as AfiliadoDisponibilidad[]) ?? []);
      setHistorial((hist.data as HistorialRow[]) ?? []);
      setNotas((notes.data as AfiliadoNota[]) ?? []);
      setServicios((servs.data as AfiliadoServicio[]) ?? []);
    } catch {
      setDataError("No pudimos cargar el detalle del afiliado. Intenta nuevamente.");
    } finally {
      setLoading(false);
    }
  }, [afiliado.id]);

  useEffect(() => {
    void load();
  }, [load]);

  const changeEstado = async (nuevoEstado: string) => {
    if (nuevoEstado === current.estado) return;
    setUpdating(true);
    setActionError("");
    try {
      const { error: updateError } = await supabase
        .from("affiliates")
        .update({ estado: nuevoEstado, actualizado_en: new Date().toISOString() })
        .eq("id", current.id);
      if (updateError) throw updateError;

      const { error: histError } = await supabase.from("affiliate_status_history").insert({
        affiliate_id: current.id,
        estado: nuevoEstado,
        nota: `Estado cambiado a ${getAfiliadoEstadoMeta(nuevoEstado).label}.`,
        autor: "administrador",
      });
      if (histError) throw histError;

      setCurrent((prev) => ({ ...prev, estado: nuevoEstado as Afiliado["estado"] }));
      onEstadoChange(current.id, nuevoEstado);
      await load();
    } catch {
      setActionError("No pudimos actualizar el estado. Intenta nuevamente.");
    } finally {
      setUpdating(false);
    }
  };

  const handleAddNote = async (nota: string) => {
    setAddingNote(true);
    setActionError("");
    try {
      const { error } = await supabase.from("affiliate_notes").insert({
        affiliate_id: current.id,
        nota,
        autor: "administrador",
      });
      if (error) throw error;
      await load();
      return true;
    } catch {
      setActionError("No pudimos guardar la nota. Intenta nuevamente.");
      return false;
    } finally {
      setAddingNote(false);
    }
  };

  const nombreMostrar =
    current.tipo === "empresa"
      ? current.empresa_nombre_comercial || current.empresa_razon_social
      : current.nombre_completo;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-foreground-950/50 p-3 md:p-6">
      <div className="w-full max-w-4xl rounded-lg border border-background-200 bg-background-100">
        {/* Encabezado */}
        <div className="sticky top-0 z-10 flex flex-wrap items-start justify-between gap-3 rounded-t-lg border-b border-background-200 bg-background-50 px-5 py-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-label font-semibold text-primary-700">{current.numero}</span>
              <AfiliadoEstadoBadge estado={current.estado} />
            </div>
            <h2 className="mt-1.5 truncate text-lg font-heading font-bold text-foreground-950">
              {nombreMostrar || "Solicitud de afiliación"}
            </h2>
            <p className="mt-0.5 text-xs text-foreground-500">
              {tipoAfiliadoLabel(current.tipo)} · Registrado el {formatFechaHora(current.creado_en)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-100 hover:text-foreground-900"
          >
            <i className="ri-close-line text-xl leading-none" aria-hidden="true"></i>
          </button>
        </div>

        <div className="space-y-5 p-5">
          {/* Contacto rápido */}
          <div className="flex flex-wrap gap-2">
            {current.telefono && (
              <a
                href={`tel:${current.telefono}`}
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm font-label font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                <i className="ri-phone-line text-base leading-none" aria-hidden="true"></i>
                Llamar
              </a>
            )}
            {current.whatsapp && (
              <a
                href={`https://wa.me/${current.whatsapp.replace(/\D/g, "")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm font-label font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                <i className="ri-whatsapp-line text-base leading-none" aria-hidden="true"></i>
                WhatsApp
              </a>
            )}
            {current.email && (
              <a
                href={`mailto:${current.email}`}
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm font-label font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                <i className="ri-mail-line text-base leading-none" aria-hidden="true"></i>
                Correo
              </a>
            )}
          </div>

          {/* Acciones de estado */}
          <div className="rounded-lg border border-background-200 bg-background-50 p-4">
            <h3 className="text-sm font-heading font-semibold text-foreground-950">Gestión de estado</h3>
            <p className="mt-1 text-xs text-foreground-500">
              Solo el administrador puede cambiar el estado. Cada cambio queda en el historial.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {acciones.map((accion) => (
                <button
                  key={accion.estado}
                  type="button"
                  onClick={() => void changeEstado(accion.estado)}
                  disabled={updating || current.estado === accion.estado}
                  className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md px-3.5 py-2 text-sm font-label font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                    accion.tone === "primary"
                      ? "bg-primary-500 text-background-50 hover:bg-primary-600 dark:text-foreground-950"
                      : "border border-background-300 text-foreground-700 hover:bg-background-100"
                  }`}
                >
                  <i className={`${accion.icon} text-base leading-none`} aria-hidden="true"></i>
                  {accion.label}
                </button>
              ))}
            </div>
          </div>

          {actionError && (
            <p className="flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-3.5 py-3 text-sm text-primary-800">
              <i className="ri-error-warning-line mt-0.5 text-base leading-none" aria-hidden="true"></i>
              {actionError}
            </p>
          )}

          {/* Pestañas */}
          <div className="flex flex-wrap gap-1.5 rounded-full border border-background-200 bg-background-50 p-1">
            {tabs.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setTab(item.id)}
                className={`inline-flex flex-1 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-full px-3 py-2 text-sm font-label font-medium transition-colors ${
                  tab === item.id
                    ? "bg-primary-500 text-background-50 dark:text-foreground-950"
                    : "text-foreground-600 hover:bg-background-100"
                }`}
              >
                <i className={`${item.icon} text-base leading-none`} aria-hidden="true"></i>
                {item.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="space-y-3">
              {[0, 1, 2].map((key) => (
                <div key={key} className="h-24 animate-pulse rounded-lg border border-background-200 bg-background-50"></div>
              ))}
            </div>
          ) : dataError ? (
            <div className="flex flex-col items-center rounded-lg border border-primary-200 bg-primary-50 px-6 py-8 text-center">
              <p className="text-sm text-primary-800">{dataError}</p>
              <button
                type="button"
                onClick={() => void load()}
                className="mt-4 inline-flex cursor-pointer items-center gap-2 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-label font-semibold text-background-50 dark:text-foreground-950"
              >
                <i className="ri-refresh-line text-base leading-none" aria-hidden="true"></i>
                Reintentar
              </button>
            </div>
          ) : (
            <>
              {tab === "resumen" && (
                <AfiliadoResumen afiliado={current} disponibilidad={disponibilidad} />
              )}
              {tab === "vehiculos" && <AfiliadoVehiculos vehiculos={vehiculos} />}
              {tab === "documentos" && (
                <AfiliadoDocumentos documentos={documentos} vehiculos={vehiculos} />
              )}
              {tab === "historial" && (
                <AfiliadoHistorial
                  historial={historial}
                  notas={notas}
                  addingNote={addingNote}
                  onAddNote={handleAddNote}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}